'use client';

import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { classnames } from '@/utils/classnames/classnames';
import { SUPPORT_EMAIL, SUPPORT_PHONE_DISPLAY, SUPPORT_PHONE_TEL } from '@/constants/support';
import type { SupportContactCardProps } from './types';

/**
 * Electus Distribution support contact card — circular primary-tinted icon
 * badge on the left, heading + description + stacked phone/email links on the
 * right. Mirrors Dan's v0 login-drawer footer layout.
 *
 * Shared between the SignInDrawer's "Need Help?" footer (EWM-267) and the
 * mobile navigation drawer's contact section (EWM-295). Phone/email come from
 * the shared `@/constants/support` module. `tel:`/`mailto:` are external
 * protocols, so raw anchors are correct here (not the locale-aware Link atom).
 */
const SupportContactCard = ({
  title,
  description,
  className,
  phone = { display: SUPPORT_PHONE_DISPLAY, tel: SUPPORT_PHONE_TEL },
  email = SUPPORT_EMAIL,
  hideIconBadge = false,
}: SupportContactCardProps) => {
  // `bg-slate-50` is the project token `--color-slate-50` (#F9FAFB) from
  // `styles/themes/default.css`, exposed there (EWM-267) precisely for surfaces
  // like this footer that must match v0's gray-50 tint — the project `gray-*`
  // scale has no 50 step and `neutral-150` (#F7F9FC) is a different colour.
  // Other tokens stay on project `gray-*` because v0's `data-theme="default"`
  // resolves those to the same project values.
  return (
    <div className={classnames('border-t border-gray-200 bg-slate-50 p-5', className)}>
      <div className="flex items-start gap-4">
        {!hideIconBadge && (
          <div
            data-testid="contact-icon-badge"
            className="bg-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
          >
            <PhoneIcon aria-hidden="true" className="h-5 w-5 text-white" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-14 mb-1 font-bold text-gray-800">{title}</h3>
          <p className="text-13 mb-2 text-gray-600">{description}</p>
          <div className="space-y-1">
            <a
              href={`tel:${phone.tel}`}
              className="text-primary hover:text-primary-hover text-14 flex min-w-0 items-center gap-2 font-semibold transition-colors"
            >
              <PhoneIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className="break-all">{phone.display}</span>
            </a>
            <a
              href={`mailto:${email}`}
              className="text-primary hover:text-primary-hover text-14 flex min-w-0 items-center gap-2 font-semibold transition-colors"
            >
              <EnvelopeIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
              <span className="break-all">{email}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportContactCard;
